"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import Image from "next/image";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import "@splidejs/splide/css";
import type { Options } from "@splidejs/splide";

type SlideEntry = {
  desktop: string;
  mobile: string | null;
  linkUrl?: string | null;
};

const FALLBACK_SLIDES: SlideEntry[] = [
  { desktop: "/images/hero/hero-banner.png", mobile: null },
  { desktop: "/images/hero/Highlight-Banner.png", mobile: null },
  { desktop: "/images/hero/Cover1.png", mobile: null },
  { desktop: "/images/hero/Cover2.png", mobile: null },
  { desktop: "/images/hero/Cover3.png", mobile: null },
  { desktop: "/images/hero/Cover4.png", mobile: null },
  { desktop: "/images/hero/Cover5.png", mobile: null },
  { desktop: "/images/hero/Cover6.png", mobile: null },
];

function normalizeAssetUrl(url: string): string {
  const t = url.trim();
  if (t.startsWith("http://") || t.startsWith("https://")) return t;
  return t.startsWith("/") ? t : `/${t}`;
}

function shouldUnoptimizedImage(src: string): boolean {
  return (
    src.startsWith("http://") ||
    src.startsWith("https://") ||
    src.startsWith("/uploads/")
  );
}

function pickMobileImageUrl(b: {
  mobileImageUrl?: string | null;
  mobile_image_url?: string | null;
}): string | null {
  const raw = b.mobileImageUrl ?? b.mobile_image_url;
  if (raw == null || String(raw).trim() === "") return null;
  return normalizeAssetUrl(String(raw).trim());
}

function mapDbBannersToSlides(
  banners: Array<{
    imageUrl?: string;
    mobileImageUrl?: string | null;
    mobile_image_url?: string | null;
    linkUrl?: string | null;
  }>
): SlideEntry[] {
  return banners
    .filter((b) => b.imageUrl && String(b.imageUrl).trim() !== "")
    .map((b) => ({
      desktop: normalizeAssetUrl(String(b.imageUrl).trim()),
      mobile: pickMobileImageUrl(b),
      linkUrl: b.linkUrl ?? null,
    }));
}

export type HeroProps = {
  initialBanners?: Array<{
    imageUrl: string;
    mobileImageUrl?: string | null;
    mobile_image_url?: string | null;
    linkUrl?: string | null;
  }> | null;
};

const SPLIDE_OPTIONS: Options = {
  type: "loop",
  autoplay: true,
  interval: 5000,
  pauseOnHover: true,
  pauseOnFocus: true,
  rewind: true,
  speed: 800,
  arrows: false,
  pagination: true,
  drag: true,
  keyboard: "global",
  perPage: 1,
  gap: 0,
  lazyLoad: "nearby",
};

export default function Hero({ initialBanners = null }: HeroProps) {
  const splideRef = useRef<any>(null);
  const [slideEntries, setSlideEntries] = useState<SlideEntry[]>(() => {
    if (initialBanners && initialBanners.length > 0) {
      const mapped = mapDbBannersToSlides(initialBanners);
      if (mapped.length > 0) return mapped;
    }
    return FALLBACK_SLIDES;
  });

  useEffect(() => {
    if (initialBanners && initialBanners.length > 0) return;

    const controller = new AbortController();
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch("/api/highlight-banners/public", {
          cache: "no-store",
          signal: controller.signal,
        });
        if (!res.ok || cancelled) return;
        const json = (await res.json()) as {
          success?: boolean;
          data?: Array<{
            imageUrl?: string;
            mobileImageUrl?: string | null;
            linkUrl?: string | null;
          }>;
        };
        if (
          cancelled ||
          !json?.success ||
          !Array.isArray(json.data) ||
          json.data.length === 0
        ) {
          return;
        }
        const mapped = mapDbBannersToSlides(json.data);
        if (mapped.length > 0) {
          setSlideEntries(mapped);
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [initialBanners]);

  const goToNext = useCallback(() => {
    splideRef.current?.go(">");
  }, []);

  const goToPrev = useCallback(() => {
    splideRef.current?.go("<");
  }, []);

  if (slideEntries.length === 0) return null;

  return (
    <section className="relative w-full overflow-hidden bg-white">
      <style jsx global>{`
        /* Hide Splide auto-generated arrows */
        .hero-splide .splide__arrows {
          display: none !important;
        }

        /* Pagination dots */

        .hero-splide .splide__pagination {
          bottom: 0.5rem;
          gap: 0.375rem;
          z-index: 20;
        }
        @media (min-width: 640px) {
        /* Hide pagination on mobile portrait */
        @media (max-width: 639px) and (orientation: portrait) {
          .hero-splide .splide__pagination {
            display: none !important;
          }
        }

          .hero-splide .splide__pagination {
            bottom: 1rem;
          }
        }
        @media (min-width: 768px) {
        /* Hide pagination on mobile portrait */
        @media (max-width: 639px) and (orientation: portrait) {
          .hero-splide .splide__pagination {
            display: none !important;
          }
        }

          .hero-splide .splide__pagination {
            bottom: 2rem;
          }
        }
        .hero-splide .splide__pagination__page {
          width: 0.5rem;
          height: 0.5rem;
          background: white;
          border-radius: 9999px;
          opacity: 1;
          border: none;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
          transition: background 0.2s;
        }
        .hero-splide .splide__pagination__page.is-active {
          background: #e50021;
          transform: scale(1);
        }

        /* Remove default Splide track margin/padding */
        .hero-splide .splide__track {
          overflow: hidden;
        }
      `}</style>

      <Splide
        ref={splideRef}
        options={SPLIDE_OPTIONS}
        className="hero-splide"
        aria-label="Hero banner carousel"
      >
        {slideEntries.map((slide, idx) => {
          const mobileSrc = slide.mobile ?? slide.desktop;
          const mobileUsesPortraitAsset = slide.mobile != null;
          const isFirstSlide = idx === 0;

          const desktopImage = isFirstSlide ? (
            <Image
              src={slide.desktop}
              alt={`Hero banner ${idx + 1}`}
              fill
              className="object-cover object-center rounded-none"
              sizes="100vw"
              priority={true}
              unoptimized={shouldUnoptimizedImage(slide.desktop)}
            />
          ) : (
            <img
              data-splide-lazy={slide.desktop}
              alt={`Hero banner ${idx + 1}`}
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
          );

          const mobileImage = isFirstSlide ? (
            <Image
              src={mobileSrc}
              alt={`Hero banner ${idx + 1}`}
              fill
              className="object-cover object-center rounded-none"
              sizes="100vw"
              priority={true}
              unoptimized={shouldUnoptimizedImage(mobileSrc)}
            />
          ) : (
            <img
              data-splide-lazy={mobileSrc}
              alt={`Hero banner ${idx + 1}`}
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
          );

          return (
            <SplideSlide key={`slide-${idx}`}>
              <div
                className={`relative hidden w-full lg:block aspect-[1920/768] bg-gray-900${
                  !slide.linkUrl ? " cursor-pointer" : ""
                }`}
                onClick={!slide.linkUrl ? goToNext : undefined}
              >
                {slide.linkUrl ? (
                  <a
                    href={slide.linkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full h-full"
                  >
                    {desktopImage}
                  </a>
                ) : (
                  desktopImage
                )}
              </div>
              <div
                className={`relative w-full overflow-hidden bg-white lg:hidden rounded-none ${
                  mobileUsesPortraitAsset ? "aspect-[4/5]" : "aspect-[16/9]"
                }${!slide.linkUrl ? " cursor-pointer" : ""}`}
                onClick={!slide.linkUrl ? goToNext : undefined}
              >
                {slide.linkUrl ? (
                  <a
                    href={slide.linkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full h-full"
                  >
                    {mobileImage}
                  </a>
                ) : (
                  mobileImage
                )}
              </div>
            </SplideSlide>
          );
        })}
      </Splide>
    </section>
  );
}
